import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider }        from './context/AuthContext';
import { LevelAccessProvider } from './context/LevelAccessContext';
import { ToastProvider }       from './context/ToastContext';

import { AppLayout }      from './components/shared/AppLayout';
import { ProtectedRoute } from './components/shared/ProtectedRoute';

// Landing & Enroll
import LandingPage from './pages/LandingPage';
import EnrollPage  from './pages/EnrollPage';

// Auth
import LoginPage          from './pages/auth/LoginPage';
import RegisterPage       from './pages/auth/RegisterPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import ResetPasswordPage  from './pages/auth/ResetPasswordPage';

// Payment
import PaymentVerifyPage  from './pages/payment/PaymentVerifyPage';
import PaymentPendingPage from './pages/payment/PaymentPendingPage';

// Student
import StudentDashboard         from './pages/student/StudentDashboard';
import CurriculumPage           from './pages/student/CurriculumPage';
import FlashcardsPage           from './pages/student/FlashcardsPage';
import MockExamPage             from './pages/student/MockExamPage';
import ResultsPage              from './pages/student/ResultsPage';
import ChooseCohortPage         from './pages/student/ChooseCohortPage';
import MaterialsPage            from './pages/student/MaterialsPage';
import NotesPage                from './pages/student/NotesPage';
import VideosPage               from './pages/student/VideosPage';
import MoviesPage               from './pages/student/MoviesPage';
import HomeworkPage             from './pages/student/HomeworkPage';
import SpeakingPage             from './pages/student/SpeakingPage';
import PruefungstrainingPage    from './pages/student/PruefungstrainingPage';
import MyTeacherPage            from './pages/student/MyTeacherPage';
import TimetablePage            from './pages/student/TimetablePage';
import AttendancePage           from './pages/student/AttendancePage';

// Teacher / Admin
import AdminDashboard             from './pages/admin/AdminDashboard';
import AdminCurriculumPage        from './pages/admin/AdminCurriculumPage';
import PaymentsPage               from './pages/admin/PaymentsPage';
import ContentPage                from './pages/admin/ContentPage';
import AdminMaterialsPage         from './pages/admin/AdminMaterialsPage';
import AdminNotesPage             from './pages/admin/AdminNotesPage';
import AdminVideosPage            from './pages/admin/AdminVideosPage';
import AdminMoviesPage            from './pages/admin/AdminMoviesPage';
import AdminHomeworkPage          from './pages/admin/AdminHomeworkPage';
import AdminSpeakingPage          from './pages/admin/AdminSpeakingPage';
import AdminPruefungstrainingPage from './pages/admin/AdminPruefungstrainingPage';
import TeacherTimetablePage       from './pages/admin/TeacherTimetablePage';
import TeacherAttendancePage      from './pages/admin/AttendancePage';

// Super Admin
import SuperAdminDashboard      from './pages/super-admin/SuperAdminDashboard';
import AssignmentsPage          from './pages/super-admin/AssignmentsPage';
import PricingPage              from './pages/super-admin/PricingPage';
import CohortsPage              from './pages/super-admin/CohortsPage';
import SuperAdminTimetablePage  from './pages/super-admin/TimetablePage';
import SuperAdminAttendancePage from './pages/super-admin/AttendancePage';

const StudentRoutes = ({ children }) => (
  <ProtectedRoute requiredRole="student">
    <LevelAccessProvider>{children}</LevelAccessProvider>
  </ProtectedRoute>
);
const TeacherRoute = ({ children }) => (
  <ProtectedRoute requiredRole="teacher">{children}</ProtectedRoute>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public — no AppLayout, no auth gate */}
      <Route path="/"                element={<LandingPage />} />
      <Route path="/enroll"          element={<EnrollPage />} />
      <Route path="/login"           element={<LoginPage />} />
      <Route path="/register"        element={<Navigate to="/enroll" replace />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/reset-password"  element={<ResetPasswordPage />} />
      <Route path="/payment/verify"  element={<PaymentVerifyPage />} />
      <Route path="/payment/pending" element={<PaymentPendingPage />} />

      {/* App shell */}
      <Route element={<AppLayout />}>
        <Route path="/student/dashboard"         element={<StudentRoutes><StudentDashboard /></StudentRoutes>} />
        <Route path="/student/choose-cohort"     element={<StudentRoutes><ChooseCohortPage /></StudentRoutes>} />
        <Route path="/student/curriculum"        element={<StudentRoutes><CurriculumPage /></StudentRoutes>} />
        <Route path="/student/flashcards"        element={<StudentRoutes><FlashcardsPage /></StudentRoutes>} />
        <Route path="/student/mock-exam"         element={<StudentRoutes><MockExamPage /></StudentRoutes>} />
        <Route path="/student/results"           element={<StudentRoutes><ResultsPage /></StudentRoutes>} />
        <Route path="/student/materials"         element={<StudentRoutes><MaterialsPage /></StudentRoutes>} />
        <Route path="/student/notes"             element={<StudentRoutes><NotesPage /></StudentRoutes>} />
        <Route path="/student/videos"            element={<StudentRoutes><VideosPage /></StudentRoutes>} />
        <Route path="/student/movies"            element={<StudentRoutes><MoviesPage /></StudentRoutes>} />
        <Route path="/student/homework"          element={<StudentRoutes><HomeworkPage /></StudentRoutes>} />
        <Route path="/student/speaking"          element={<StudentRoutes><SpeakingPage /></StudentRoutes>} />
        <Route path="/student/pruefungstraining" element={<StudentRoutes><PruefungstrainingPage /></StudentRoutes>} />
        <Route path="/student/timetable"         element={<StudentRoutes><TimetablePage /></StudentRoutes>} />
        <Route path="/student/attendance"        element={<StudentRoutes><AttendancePage /></StudentRoutes>} />
        <Route path="/student/my-teacher"        element={<StudentRoutes><MyTeacherPage /></StudentRoutes>} />

        <Route path="/admin/dashboard"           element={<TeacherRoute><AdminDashboard /></TeacherRoute>} />
        <Route path="/admin/curriculum"          element={<TeacherRoute><AdminCurriculumPage /></TeacherRoute>} />
        <Route path="/admin/payments"            element={<TeacherRoute><PaymentsPage /></TeacherRoute>} />
        <Route path="/admin/content"             element={<TeacherRoute><ContentPage /></TeacherRoute>} />
        <Route path="/admin/materials"           element={<TeacherRoute><AdminMaterialsPage /></TeacherRoute>} />
        <Route path="/admin/notes"               element={<TeacherRoute><AdminNotesPage /></TeacherRoute>} />
        <Route path="/admin/videos"              element={<TeacherRoute><AdminVideosPage /></TeacherRoute>} />
        <Route path="/admin/movies"              element={<TeacherRoute><AdminMoviesPage /></TeacherRoute>} />
        <Route path="/admin/homework"            element={<TeacherRoute><AdminHomeworkPage /></TeacherRoute>} />
        <Route path="/admin/speaking"            element={<TeacherRoute><AdminSpeakingPage /></TeacherRoute>} />
        <Route path="/admin/pruefungstraining"   element={<TeacherRoute><AdminPruefungstrainingPage /></TeacherRoute>} />
        <Route path="/admin/timetable"           element={<TeacherRoute><TeacherTimetablePage /></TeacherRoute>} />
        <Route path="/admin/attendance"          element={<TeacherRoute><TeacherAttendancePage /></TeacherRoute>} />

        <Route path="/super-admin/dashboard"    element={<ProtectedRoute requiredRole="super_admin"><SuperAdminDashboard /></ProtectedRoute>} />
        <Route path="/super-admin/assignments"  element={<ProtectedRoute requiredRole="super_admin"><AssignmentsPage /></ProtectedRoute>} />
        <Route path="/super-admin/pricing"      element={<ProtectedRoute requiredRole="super_admin"><PricingPage /></ProtectedRoute>} />
        <Route path="/super-admin/cohorts"      element={<ProtectedRoute requiredRole="super_admin"><CohortsPage /></ProtectedRoute>} />
        <Route path="/super-admin/timetable"    element={<ProtectedRoute requiredRole="super_admin"><SuperAdminTimetablePage /></ProtectedRoute>} />
        <Route path="/super-admin/attendance"   element={<ProtectedRoute requiredRole="super_admin"><SuperAdminAttendancePage /></ProtectedRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}